import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Plus, Minus } from "lucide-react";

const cls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-2.5 text-sm outline-none text-[color:var(--ms-charcoal)]";
const F = ({ label, children }) => (<div><label className="overline block mb-2">{label}</label>{children}</div>);

export default function AdminHomepage() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const logoRef = useRef(null);
    const heroRef = useRef(null);
    const completiRef = useRef(null);
    const dropRef = useRef(null);
    const clothingRef = useRef(null);
    const footwearRef = useRef(null);

    useEffect(() => { api.get("/homepage").then(r => setData(r.data)); }, []);

    if (!data) return <div className="text-[color:var(--ms-muted)]">Caricamento…</div>;

    const set = (key, value) => setData(prev => ({ ...prev, [key]: value }));
    const setTrust = (idx, val) => set("trust_items", data.trust_items.map((v, i) => i === idx ? val : v));
    const setWhy = (idx, key, val) => set("why_items", data.why_items.map((v, i) => i === idx ? { ...v, [key]: val } : v));
    const setAgeCard = (idx, key, val) => set("shop_by_age", data.shop_by_age.map((v, i) => i === idx ? { ...v, [key]: val } : v));

    const addTrust = () => set("trust_items", [...data.trust_items, "Nuovo vantaggio"]);
    const removeTrust = (idx) => set("trust_items", data.trust_items.filter((_, i) => i !== idx));
    const addWhy = () => set("why_items", [...data.why_items, { title: "Nuovo motivo", text: "Descrivi il perché."} ]);
    const removeWhy = (idx) => set("why_items", data.why_items.filter((_, i) => i !== idx));
    const addAgeCard = () => set("shop_by_age", [...data.shop_by_age, { name: "Categoria", slug: "slug", image: "", age: "" }]);
    const removeAgeCard = (idx) => set("shop_by_age", data.shop_by_age.filter((_, i) => i !== idx));

    const upload = async (file, onUrl) => {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUrl(r.data.url);
    };

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/admin/homepage", data);
            setData(r.data);
            toast.success("Homepage salvata");
        } catch (e) {
            toast.error("Salvataggio fallito");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-homepage">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <div className="overline mb-2">Contenuto</div>
                    <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Editor Homepage</h1>
                </div>
                <button onClick={save} disabled={saving} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)] disabled:opacity-50" data-testid="homepage-save">{saving ? "SALVATAGGIO…" : "Salva Modifiche"}</button>
            </div>

            <div className="space-y-8">
                <Section title="Logo & Brand">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Nome logo"><input value={data.logo_text} onChange={e => set("logo_text", e.target.value)} className={cls} data-testid="logo-text" /></F>
                        <F label="Sottotitolo logo"><input value={data.logo_tagline || ""} onChange={e => set("logo_tagline", e.target.value)} className={cls} /></F>
                        <F label="Immagine logo (sostituisce il testo)">
                            <div className="flex gap-2">
                                <input value={data.logo_image_url || ""} onChange={e => set("logo_image_url", e.target.value)} placeholder="/api/files/… oppure URL" className={cls} data-testid="logo-image-url" />
                                <button onClick={() => logoRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("logo_image_url", url))} />
                            </div>
                        </F>
                    </div>
                </Section>

                <Section title="Hero">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Titolo principale"><textarea rows={2} value={data.hero_headline} onChange={e => set("hero_headline", e.target.value)} className={cls} data-testid="hero-headline" /></F>
                        <F label="Sottotitolo"><textarea rows={2} value={data.hero_subheadline} onChange={e => set("hero_subheadline", e.target.value)} className={cls} /></F>
                        <F label="Immagine Hero">
                            <div className="flex gap-2">
                                <input value={data.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} className={cls} />
                                <button onClick={() => heroRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("hero_image_url", url))} />
                            </div>
                        </F>
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Testo CTA primario"><input value={data.hero_cta_primary} onChange={e => set("hero_cta_primary", e.target.value)} className={cls} /></F>
                            <F label="Link CTA primario"><input value={data.hero_cta_primary_link} onChange={e => set("hero_cta_primary_link", e.target.value)} className={cls} /></F>
                            <F label="Testo CTA secondario"><input value={data.hero_cta_secondary} onChange={e => set("hero_cta_secondary", e.target.value)} className={cls} /></F>
                            <F label="Link CTA secondario"><input value={data.hero_cta_secondary_link} onChange={e => set("hero_cta_secondary_link", e.target.value)} className={cls} /></F>
                        </div>
                    </div>
                </Section>

                <Section title="Trust banner" onAdd={addTrust}>
                    {data.trust_items.map((t, i) => (
                        <div key={i} className="flex gap-2">
                            <input value={t} onChange={e => setTrust(i, e.target.value)} className={cls} />
                            <button onClick={() => removeTrust(i)} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-terracotta)]"><Minus size={14} /></button>
                        </div>
                    ))}
                </Section>

                <Section title="Shop per età (cards homepage)" onAdd={addAgeCard}>
                    {data.shop_by_age.map((b, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_2fr_auto] gap-2 items-center">
                            <input value={b.name} onChange={e => setAgeCard(i, "name", e.target.value)} placeholder="Nome" className={cls} />
                            <input value={b.slug} onChange={e => setAgeCard(i, "slug", e.target.value)} placeholder="slug (senza /)" className={cls} />
                            <input value={b.age || ""} onChange={e => setAgeCard(i, "age", e.target.value)} placeholder="Fascia (es. 0-24 mesi)" className={cls} />
                            <input value={b.image} onChange={e => setAgeCard(i, "image", e.target.value)} placeholder="URL immagine" className={cls} />
                            <button onClick={() => removeAgeCard(i)} className="border border-[color:var(--ms-border)] px-3 py-2.5 hover:border-[color:var(--ms-terracotta)]"><Minus size={14} /></button>
                        </div>
                    ))}
                </Section>

                <Section title="Completi (blocco editoriale)">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Titolo"><input value={data.completi_title} onChange={e => set("completi_title", e.target.value)} className={cls} /></F>
                        <F label="Sottotitolo"><textarea rows={2} value={data.completi_subtitle} onChange={e => set("completi_subtitle", e.target.value)} className={cls} /></F>
                        <F label="Immagine">
                            <div className="flex gap-2">
                                <input value={data.completi_image} onChange={e => set("completi_image", e.target.value)} className={cls} />
                                <button onClick={() => completiRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={completiRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("completi_image", url))} />
                            </div>
                        </F>
                        <F label="Link"><input value={data.completi_link} onChange={e => set("completi_link", e.target.value)} className={cls} /></F>
                        <F label="Testo CTA"><input value={data.completi_cta} onChange={e => set("completi_cta", e.target.value)} className={cls} /></F>
                    </div>
                </Section>

                <Section title="Abbigliamento & Calzature (split)">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <F label="Titolo Abbigliamento"><input value={data.clothing_title} onChange={e => set("clothing_title", e.target.value)} className={cls} /></F>
                            <F label="Link Abbigliamento"><input value={data.clothing_link} onChange={e => set("clothing_link", e.target.value)} className={cls} /></F>
                            <F label="Immagine Abbigliamento">
                                <div className="flex gap-2">
                                    <input value={data.clothing_image} onChange={e => set("clothing_image", e.target.value)} className={cls} />
                                    <button onClick={() => clothingRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                    <input ref={clothingRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("clothing_image", url))} />
                                </div>
                            </F>
                        </div>
                        <div className="space-y-3">
                            <F label="Titolo Calzature"><input value={data.footwear_title} onChange={e => set("footwear_title", e.target.value)} className={cls} /></F>
                            <F label="Link Calzature"><input value={data.footwear_link} onChange={e => set("footwear_link", e.target.value)} className={cls} /></F>
                            <F label="Immagine Calzature">
                                <div className="flex gap-2">
                                    <input value={data.footwear_image} onChange={e => set("footwear_image", e.target.value)} className={cls} />
                                    <button onClick={() => footwearRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                    <input ref={footwearRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("footwear_image", url))} />
                                </div>
                            </F>
                        </div>
                    </div>
                </Section>

                <Section title="Featured Collection (editoriale)">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Titolo"><input value={data.featured_drop_title} onChange={e => set("featured_drop_title", e.target.value)} className={cls} /></F>
                        <F label="Sottotitolo"><input value={data.featured_drop_subtitle} onChange={e => set("featured_drop_subtitle", e.target.value)} className={cls} /></F>
                        <F label="Immagine">
                            <div className="flex gap-2">
                                <input value={data.featured_drop_image} onChange={e => set("featured_drop_image", e.target.value)} className={cls} />
                                <button onClick={() => dropRef.current?.click()} className="border border-[color:var(--ms-border)] px-3 hover:border-[color:var(--ms-charcoal)]"><Upload size={14} /></button>
                                <input ref={dropRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(e.target.files[0], url => set("featured_drop_image", url))} />
                            </div>
                        </F>
                        <F label="Link"><input value={data.featured_drop_link} onChange={e => set("featured_drop_link", e.target.value)} className={cls} /></F>
                    </div>
                </Section>

                <Section title="Perché Mini Style" onAdd={addWhy}>
                    {data.why_items.map((w, i) => (
                        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2">
                            <input value={w.title} onChange={e => setWhy(i, "title", e.target.value)} placeholder="Titolo" className={cls} />
                            <input value={w.text} onChange={e => setWhy(i, "text", e.target.value)} placeholder="Descrizione" className={cls} />
                            <button onClick={() => removeWhy(i)} className="border border-[color:var(--ms-border)] px-3 py-2.5 hover:border-[color:var(--ms-terracotta)]"><Minus size={14} /></button>
                        </div>
                    ))}
                </Section>

                <Section title="Newsletter">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Titolo"><input value={data.newsletter_headline} onChange={e => set("newsletter_headline", e.target.value)} className={cls} /></F>
                        <F label="Offerta / Descrizione"><input value={data.newsletter_offer} onChange={e => set("newsletter_offer", e.target.value)} className={cls} /></F>
                    </div>
                </Section>

                <Section title="Instagram">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Handle Instagram"><input value={data.instagram_handle} onChange={e => set("instagram_handle", e.target.value)} className={cls} /></F>
                        <F label="URL Instagram"><input value={data.footer_instagram || ""} onChange={e => set("footer_instagram", e.target.value)} className={cls} /></F>
                        <F label="Embed Instagram (HTML)"><textarea rows={4} value={data.instagram_embed} onChange={e => set("instagram_embed", e.target.value)} className={cls} placeholder="<blockquote class='instagram-media' …>" /></F>
                    </div>
                </Section>

                <Section title="Footer">
                    <div className="grid md:grid-cols-2 gap-5">
                        <F label="Chi siamo (breve)"><textarea rows={3} value={data.footer_about} onChange={e => set("footer_about", e.target.value)} className={cls} /></F>
                        <F label="Email di contatto"><input value={data.footer_email} onChange={e => set("footer_email", e.target.value)} className={cls} /></F>
                    </div>
                </Section>
            </div>

            <div className="mt-10 flex justify-end">
                <button onClick={save} disabled={saving} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)] disabled:opacity-50">{saving ? "SALVATAGGIO…" : "Salva Modifiche"}</button>
            </div>
        </div>
    );
}

const Section = ({ title, children, onAdd }) => (
    <div className="panel p-6">
        <div className="flex justify-between items-center mb-5">
            <h2 className="font-display text-2xl text-[color:var(--ms-charcoal)]">{title}</h2>
            {onAdd && <button onClick={onAdd} className="border border-[color:var(--ms-charcoal)] text-[color:var(--ms-charcoal)] px-3 py-2 text-xs tracking-[0.2em] uppercase flex items-center gap-1 hover:bg-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-warm-white)]"><Plus size={12} /> Aggiungi</button>}
        </div>
        <div className="space-y-3">{children}</div>
    </div>
);
